/**
 * WizardShell
 *
 * Reusable wrapper for every wizard in the platform. Owns the Modal frame,
 * the WizardStepper sub-header, and the footer action buttons. Wizard-specific
 * screen content is passed as children; button behaviour is driven by the
 * `actions` config prop so individual wizards never need to import Button or
 * arrow-icon components.
 *
 * Props
 * ─────
 * open          bool     – Controls Modal visibility
 * onClose       fn       – Called when the modal requests dismissal
 * title         string   – Modal header title
 * width         string   – Modal width token passed to Modal ('sm'|'md'|'lg'). Default 'md'
 * steps         string[] – Step labels passed to WizardStepper
 * activeStep    number   – 0-based active step index
 * showStepper   bool     – Whether to render the stepper in the header
 * isGenerating  bool     – When true, hides stepper and footer (operation in-flight)
 * actions       object   – { primary, secondary } — each shape:
 *                            { label, onClick, disabled?, icon? }
 *                          Pass null to render no footer.
 *                          icon is one of: 'forward' | 'back' | 'generate' — the
 *                          shell resolves the MUI icon internally.
 * children      node     – Active screen content
 *
 * Usage
 * ─────
 * <WizardShell
 *   open={open}
 *   onClose={onClose}
 *   title={MODAL_TITLE[screen]}
 *   steps={WIZARD_STEPS}
 *   activeStep={STEPPER_INDEX[screen]}
 *   showStepper={showStepper}
 *   isGenerating={isGenerating}
 *   actions={getActions(screen)}
 * >
 *   {renderContent()}
 * </WizardShell>
 */

import { Box, Button, Divider }  from '@mui/material';
import Modal            from '../../ui/layout/Modal';
import WizardStepper    from './WizardStepper';

const WizardShell = ({
  open,
  onClose,
  title,
  width = 'md',
  steps,
  activeStep,
  showStepper = true,
  isGenerating = false,
  actions,
  children,
}) => {
  const renderFooter = () => {
    const { primary, secondary } = actions;
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {secondary && (
          <Button variant="outlined" color="secondary" startIcon={secondary.startIcon} onClick={secondary.onClick} disabled={secondary.disabled}>
            {secondary.label}
          </Button>
        )}
        {primary && (
          <Button variant="contained" startIcon={primary.startIcon} endIcon={primary.endIcon} onClick={primary.onClick} disabled={primary.disabled}>
            {primary.label}
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Modal
      open={open}
      handleClose={!isGenerating ? onClose : undefined}
      modalTitle={title}
      width={width}
      // disableBackdropClose={isGenerating}
      action={actions ? renderFooter : undefined}
    >
      {!isGenerating && showStepper && steps?.length > 0 && (
        <><WizardStepper steps={steps} activeStep={activeStep} labelSize="11px" />
        <Divider sx={{ my: 2 }} /></>
      )}
      {children}
    </Modal>
  );
};

export default WizardShell;